import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';

interface Tipster {
  id: string;
  username: string;
  avatar_url: string;
  roi: number;
  total_picks: number;
  won_picks: number;
  sport: string;
  speciality: string;
  is_verified: boolean;
  followers: number;
  ytd_profit: string;
  badge: string;
  badge_color: string;
}

function TipsterCardLarge({ tipster, rank }: { tipster: Tipster; rank: number }) {
  const hitRate =
    tipster.total_picks > 0
      ? ((tipster.won_picks / tipster.total_picks) * 100).toFixed(1)
      : '0.0';

  return (
    <div
      className={`relative bg-[#151719] border rounded-2xl p-6 overflow-hidden group hover:border-[#00e87a]/30 transition-all duration-300 ${
        rank === 1 ? 'border-[#ffd700]/30' : 'border-[#2a2d3a]'
      }`}
    >
      {rank === 1 && (
        <div className="absolute top-0 right-0 px-3 py-1 bg-[#ffd700]/10 rounded-bl-xl">
          <span className="text-[#ffd700] text-xs font-bold">#1 GLOBAL</span>
        </div>
      )}
      <div className="flex items-start gap-4 mb-5">
        <div className="relative flex-shrink-0">
          {tipster.avatar_url ? (
            <img
              src={tipster.avatar_url}
              alt={tipster.username}
              className="w-12 h-12 rounded-full object-cover border-2"
              style={{ borderColor: tipster.badge_color || '#a8b2c1' }}
            />
          ) : (
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center border-2 text-lg font-black"
              style={{
                borderColor: tipster.badge_color || '#a8b2c1',
                background: `${tipster.badge_color || '#a8b2c1'}20`,
                color: tipster.badge_color || '#a8b2c1',
              }}
            >
              {tipster.username.charAt(0).toUpperCase()}
            </div>
          )}
          {tipster.is_verified && (
            <div className="absolute -bottom-1 -right-1 w-5 h-5 flex items-center justify-center bg-[#00e87a] rounded-full">
              <i className="ri-check-line text-black text-xs"></i>
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-bold text-base">{tipster.username}</p>
          <p className="text-[#6b7280] text-xs">{tipster.speciality || tipster.sport}</p>
          <span
            className="inline-block px-2 py-0.5 rounded-full text-xs font-bold mt-1"
            style={{
              background: `${tipster.badge_color || '#a8b2c1'}20`,
              color: tipster.badge_color || '#a8b2c1',
            }}
          >
            {tipster.badge || 'Nuevo'}
          </span>
        </div>
        <div className="text-right">
          <p className="text-[#00e87a] text-xl font-black">
            {tipster.roi > 0 ? '+' : ''}{tipster.roi}%
          </p>
          <p className="text-[#6b7280] text-xs">ROI</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-[#0d0f14] rounded-lg p-3 text-center">
          <p className="text-white font-bold text-lg">{hitRate}%</p>
          <p className="text-[#6b7280] text-xs">Aciertos</p>
        </div>
        <div className="bg-[#0d0f14] rounded-lg p-3 text-center">
          <p className="text-white font-bold text-lg">{tipster.total_picks}</p>
          <p className="text-[#6b7280] text-xs">Picks</p>
        </div>
        <div className="bg-[#0d0f14] rounded-lg p-3 text-center">
          <p className="text-[#00e87a] font-bold text-lg">{tipster.ytd_profit || '—'}</p>
          <p className="text-[#6b7280] text-xs">Beneficio</p>
        </div>
      </div>

      <div className="flex items-center justify-between text-xs text-[#6b7280]">
        <span className="flex items-center gap-1">
          <div className="w-3 h-3 flex items-center justify-center">
            <i className="ri-group-line text-xs"></i>
          </div>
          {tipster.followers.toLocaleString('es-ES')} seguidores
        </span>
        <span className="text-[#a8b2c1] text-xs">{tipster.sport}</span>
      </div>
    </div>
  );
}

function TipsterCardSmall({ tipster }: { tipster: Tipster }) {
  const hitRate =
    tipster.total_picks > 0
      ? ((tipster.won_picks / tipster.total_picks) * 100).toFixed(1)
      : '0.0';

  return (
    <div className="bg-[#151719] border border-[#2a2d3a] rounded-2xl p-5 flex items-center gap-4 hover:border-[#00e87a]/20 transition-all cursor-pointer">
      {tipster.avatar_url ? (
        <img
          src={tipster.avatar_url}
          alt={tipster.username}
          className="w-10 h-10 rounded-full object-cover flex-shrink-0"
        />
      ) : (
        <div className="w-10 h-10 rounded-full bg-[#2a2d3a] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
          {tipster.username.charAt(0).toUpperCase()}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-white font-semibold text-sm">{tipster.username}</p>
        <p className="text-[#6b7280] text-xs">{tipster.speciality || tipster.sport}</p>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-[#00e87a] font-bold">
          {tipster.roi > 0 ? '+' : ''}{tipster.roi}%
        </p>
        <p className="text-[#6b7280] text-xs">{hitRate}% acc.</p>
      </div>
    </div>
  );
}

export default function TipsterShowcase() {
  const [tipsters, setTipsters] = useState<Tipster[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTop = async () => {
      const { data } = await supabase
        .from('profiles')
        .select('id, username, avatar_url, roi, total_picks, won_picks, sport, speciality, is_verified, followers, ytd_profit, badge, badge_color')
        .order('roi', { ascending: false })
        .limit(6);

      setTipsters((data as Tipster[]) ?? []);
      setLoading(false);
    };

    fetchTop();
  }, []);

  const top3 = tipsters.slice(0, 3);
  const rest = tipsters.slice(3, 6);

  return (
    <section className="py-24 bg-[#101218]">
      <div className="max-w-[1440px] mx-auto px-6">
        <div className="text-center mb-14">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#ffd700]/10 border border-[#ffd700]/20 text-[#ffd700] text-xs font-bold uppercase tracking-widest mb-4">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-vip-crown-line text-sm"></i>
            </div>
            Tipsters Verificados
          </span>
          <h2 className="text-white text-4xl md:text-5xl font-black uppercase">
            LA COMUNIDAD MÁS<br />
            <span className="text-[#00e87a]">RENTABLE DE ESPAÑA</span>
          </h2>
          <p className="text-[#a8b2c1] text-base mt-4 max-w-xl mx-auto">
            Cada predicción queda registrada. Cada resultado, verificado. El ROI no miente.
          </p>
        </div>

        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-[#151719] border border-[#2a2d3a] rounded-2xl p-6 animate-pulse">
                <div className="flex items-start gap-4 mb-5">
                  <div className="w-12 h-12 rounded-full bg-[#2a2d3a] flex-shrink-0"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-[#2a2d3a] rounded w-3/4"></div>
                    <div className="h-3 bg-[#2a2d3a] rounded w-1/2"></div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {Array.from({ length: 3 }).map((__, j) => (
                    <div key={j} className="h-16 bg-[#2a2d3a] rounded-lg"></div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && tipsters.length === 0 && (
          <div className="bg-[#151719] border border-[#2a2d3a] rounded-2xl p-16 text-center mb-10">
            <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#ffd700]/10 mx-auto mb-4">
              <i className="ri-trophy-line text-[#ffd700] text-3xl"></i>
            </div>
            <p className="text-white font-bold text-lg">El ranking está por construirse</p>
            <p className="text-[#a8b2c1] text-sm mt-2 max-w-sm mx-auto">
              Regístrate, comparte tus picks y escala posiciones. El ranking se actualiza en tiempo real.
            </p>
            <Link
              to="/ranking"
              className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-[#ffd700] text-black font-black text-sm rounded-xl cursor-pointer hover:bg-[#f0c800] transition-all"
            >
              Ver Ranking
            </Link>
          </div>
        )}

        {!loading && top3.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
            {top3.map((t, i) => (
              <TipsterCardLarge key={t.id} tipster={t} rank={i + 1} />
            ))}
          </div>
        )}

        {!loading && rest.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
            {rest.map((t) => (
              <TipsterCardSmall key={t.id} tipster={t} />
            ))}
          </div>
        )}

        <div className="text-center">
          <Link
            to="/ranking"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#151719] border border-[#2a2d3a] text-white font-semibold rounded-xl hover:border-[#00e87a]/30 hover:text-[#00e87a] transition-all cursor-pointer"
          >
            <span>Ver Ranking Completo</span>
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-right-line"></i>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}
